import type { BlogLang, BlogPost, BlogPostSummary, BlogTagCount } from '../api/blog';
import type { Paged } from '../api/income';

/**
 * What the server already knew when it rendered this page.
 *
 * A blog URL is answered by the server with the article written into the document — crawlers that
 * run no JavaScript need it there — and beside it, in a `<script type="application/json">`, the
 * same data the API would return. Reading that block lets the first render draw the article
 * immediately instead of showing a blank frame, fetching, and drawing it a moment later: the
 * markup React replaces and the markup it replaces it with are the same article.
 *
 * It is only ever valid for the page that was requested. Each consumer therefore states what it
 * expects — language, slug, page, tag — and gets nothing back when the block describes something
 * else, which is what happens after a client-side navigation to another article.
 */
export type InitialData =
  | { kind: 'post'; lang: BlogLang; slug: string; post: BlogPost }
  | {
      kind: 'index';
      lang: BlogLang;
      tag: string | null;
      page: number;
      posts: Paged<BlogPostSummary>;
      tags: BlogTagCount[];
    };

const ELEMENT_ID = 'danak-initial';

let parsed: InitialData | null | undefined;

function read(): InitialData | null {
  if (parsed !== undefined) {
    return parsed;
  }

  try {
    const text = document.getElementById(ELEMENT_ID)?.textContent;
    parsed = text ? (JSON.parse(text) as InitialData) : null;
  } catch {
    // A block that does not parse is the same as no block: the page fetches like any other.
    parsed = null;
  }

  return parsed;
}

export function initialPost(lang: BlogLang, slug: string): BlogPost | undefined {
  const data = read();
  return data?.kind === 'post' && data.lang === lang && data.slug === slug ? data.post : undefined;
}

export function initialIndex(lang: BlogLang, tag: string | null, page: number) {
  const data = read();

  return data?.kind === 'index' && data.lang === lang && (data.tag ?? null) === tag && data.page === page
    ? { posts: data.posts, tags: data.tags }
    : undefined;
}

/** Tests render several pages in one document; each needs to start from its own block. */
export function resetInitialDataForTests() {
  parsed = undefined;
}
